import React, { useEffect, useState } from "react";
import "./FieldList.css";
import { AiFillPlusCircle } from "react-icons/ai";
import Header from "../header/Header";
import {
  getAllFields,
  AddField,
  DeleteField,
  UpdateField,
} from "../../services/ApiService";
import {
  Button,
  Form,
  Input,
  Modal,
  message,
  Table,
  Row,
  Col,
  Switch,
  Select,
} from "antd";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import Sidebar from "../Sidebar/Sidebar";
import { hasPermission } from "../GlobalFunction";

const FieldList = () => {
  const canCreate = hasPermission("fields", "create");
  const canUpdate = hasPermission("fields", "update");
  const canDelete = hasPermission("fields", "delete");
  const [fields, setFields] = useState([]);
  const [selectedFieldData, setSelectedFieldData] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [searchName, setSearchName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(undefined);
  const [form] = Form.useForm();

  const categoryOptions = Array.from(
    new Set(fields.map((field) => field.category).filter(Boolean))
  )
    .sort((a, b) => a.localeCompare(b))
    .map((category) => ({ label: category, value: category }));

  const filteredFields = fields.filter((field) => {
    const matchesName = searchName
      ? (field.name || "").toLowerCase().includes(searchName.toLowerCase())
      : true;
    const matchesCategory = selectedCategory
      ? field.category === selectedCategory
      : true;
    return matchesName && matchesCategory;
  });

  const handleClearFilters = () => {
    setSearchName("");
    setSelectedCategory(undefined);
  };

  const fetchFields = async () => {
    try {
      const res = await getAllFields();
      if (res.response_code === 200) {
        setFields(res.field_list);
      } else {
        throw new Error("Failed to fetch fields");
      }
    } catch (error) {
      console.error("Error fetching fields:", error);
      message.error("Failed to fetch fields");
    }
  };

  useEffect(() => {
    fetchFields();
  }, []);

  const handleEditFieldClick = (record) => {
    setModalMode("edit");
    setSelectedFieldData(record);
    const parsedJson = JSON.parse(record?.json);
    form.setFieldsValue({
      name: record?.name,
      category: record?.category,
      step: record?.step,
      required: record?.required === 1,
      json: JSON.stringify(parsedJson, null, 2),
    });
    setModalVisible(true);
  };

  const handleDeleteFieldClick = async (id) => {
    try {
      const res = await DeleteField(id);
      if (res.response_code === 200) {
        message.success("Field deleted successfully");
        setFields(fields.filter((field) => field.id !== id));
      } else {
        throw new Error("Failed to delete field");
      }
    } catch (error) {
      message.error("Failed to delete field");
      console.error("Error deleting field:", error);
    }
  };

  const handleAdd = () => {
    setModalMode("add");
    setModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      console.log(values, "val");
      if (modalMode === "add") {
        const body = {
          name: values.name,
          json: JSON.parse(values.json),
          category: values.category,
          step: values.step,
          required: values.required,
        };
        const res = await AddField(body);
        if (res.response_code === 200) {
          message.success("Field added successfully");
          fetchFields();
        } else {
          throw new Error("Failed to add field");
        }
      } else if (modalMode === "edit" && selectedFieldData) {
        const body = {
          id: selectedFieldData.id,
          name: values.name,
          category: values.category,
          json: JSON.parse(values.json),
          step: values.step,
          required: values.required,
        };
        const res = await UpdateField(body);
        if (res.response_code === 200) {
          message.success("Field updated successfully");
          setFields(
            fields.map((field) =>
              field.id === selectedFieldData.id
                ? { ...field, ...values }
                : field
            )
          );
          setSelectedFieldData(null);
        } else {
          throw new Error("Failed to update field");
        }
      }
      setModalVisible(false);
    } catch (error) {
      if (error instanceof SyntaxError) {
        message.error(`Invalid JSON: ${error.message}`);
      } else {
        message.error("Failed to save field");
      }
      console.error("Error saving field:", error);
    }
  };

  const handleModalCancel = () => {
    form.setFieldsValue({
      name: "",
      category: "",
      step: "",
      required: false,
      json: "",
    });
    setModalVisible(false);
  };

  const columns = [
    {
      title: "Sr. No",
      dataIndex: "id",
      key: "id",
      render: (text, record, index) => index + 1,
      responsive: ["md"],
    },
    {
      title: "Field Name",
      dataIndex: "name",
      key: "name",
      responsive: ["xs", "sm", "md"],
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      responsive: ["xs", "sm", "md"],
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          {canUpdate && (
            <Button
              type="primary"
              onClick={() => handleEditFieldClick(record)}
              style={{ marginRight: "0.5rem" }}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              color="danger"
              onClick={() => handleDeleteFieldClick(record.id)}
            >
              Delete
            </Button>
          )}
        </>
      ),
      responsive: ["xs", "sm", "md"],
    },
  ];

  return (
    <>
      <div className="main-box">
        <Sidebar activeId={6} />
        <main className="main-content">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
            }}
          >
            <Header title="Fields Management" />
            <div
              style={{
                flex: 1,
                padding: "1.5rem",
                boxSizing: "border-box",
                alignItems: "center",
                justifyContent: "center",
                overflowY: "auto",
              }}
            >
              {canCreate && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <div className="field-box" onClick={handleAdd}>
                    <AiFillPlusCircle color="#534ECC" />
                    <span
                      style={{
                        color: "#344767",
                        fontSize: "1rem",
                        fontWeight: 700,
                      }}
                    >
                      {" "}
                      Add Field{" "}
                    </span>
                  </div>
                </div>
              )}
              <p
                style={{
                  textAlign: "center",
                  fontFamily: "Inter",
                  fontSize: "0.8rem",
                  color: "#7B809A",
                }}
              >
                Any changes you make here will apply across all country forms.
                Add, edit, or remove fields to ensure consistency and accuracy
                in the onboarding process.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginTop: "1rem",
                }}
              >
                <Input
                  placeholder="Search by field name"
                  prefix={<SearchOutlined />}
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  allowClear
                  style={{ width: 220, fontSize: "0.8rem" }}
                />
                <Select
                  placeholder="Filter by category"
                  showSearch
                  allowClear
                  value={selectedCategory}
                  onChange={(value) => setSelectedCategory(value)}
                  options={categoryOptions}
                  optionFilterProp="label"
                  style={{ width: 220 }}
                />
                {(searchName || selectedCategory) && (
                  <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
                    Clear Filters
                  </Button>
                )}
              </div>
              <Table
                columns={columns}
                dataSource={filteredFields}
                rowKey="id"
                pagination={false}
                scroll={{ x: "max-content" }}
                style={{
                  height: "78%",
                  overflowY: "auto",
                  marginTop: "1rem",
                  fontSize: "0.8rem",
                  borderRadius: "1.5rem",
                  background: "#FFF",
                  boxShadow: "0px 1.72px 5.161px 0px rgba(0, 0, 0, 0.25)",
                }}
                className="custom-scrollbar"
              />
            </div>
          </div>
        </main>

        <Modal
          title={
            <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              {modalMode === "add" ? "Create New Field" : "Edit Field"}
            </span>
          }
          centered
          open={modalVisible}
          closable={false}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
        >
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label={
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "500",
                        color: "#6F6C90",
                      }}
                    >
                      Field Name
                    </span>
                  }
                  className="input-label"
                  rules={[
                    { required: true, message: "Field name is required" },
                  ]}
                >
                  <Input
                    placeholder="Enter field name"
                    className="input-box"
                    readOnly={modalMode === "edit"}
                    style={{ fontSize: "0.8rem" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="category"
                  className="input-label"
                  label={
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "500",
                        color: "#6F6C90",
                      }}
                    >
                      Field Category Type
                    </span>
                  }
                  rules={[
                    { required: true, message: "Category Type is required" },
                  ]}
                >
                  <Input
                    placeholder="Enter Category Name"
                    className="input-box"
                    style={{ fontSize: "0.8rem" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="step"
                  label={
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "500",
                        color: "#6F6C90",
                      }}
                    >
                      Step Number
                    </span>
                  }
                  className="input-label"
                  rules={[
                    { required: true, message: "Step number is required" },
                  ]}
                >
                  <Input
                    type="number"
                    placeholder="Enter field name"
                    className="input-box"
                    style={{ fontSize: "0.8rem" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="required"
                  className="input-label"
                  label={
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "500",
                        color: "#6F6C90",
                      }}
                    >
                      Required
                    </span>
                  }
                  valuePropName="checked"
                  getValueFromEvent={(checked) => (checked ? 1 : 0)}
                  initialValue={0}
                  rules={[
                    { required: true, message: "This field is required" },
                  ]}
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="json"
              className="input-label"
              label={
                <span
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    color: "#6F6C90",
                  }}
                >
                  Field JSON
                </span>
              }
              rules={[
                { required: true, message: "Field JSON is required" },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    try {
                      JSON.parse(value);
                      return Promise.resolve();
                    } catch (err) {
                      return Promise.reject(new Error(`Invalid JSON: ${err.message}`));
                    }
                  },
                },
              ]}
            >
              <Input.TextArea
                placeholder="Enter field JSON"
                className="input-box custom-scrollbar"
                rows={4}
                style={{ fontSize: "0.8rem" }}
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default FieldList;
